import { useState, useEffect } from "react";
import { $host } from "../../../../http";
import ThemeCardSmall from "../../components/cards/ThemeCardSmall";
import CreateThemeModal from "./CreateThemeModal"; // Импортируем модалку создания темы

const SelectThemeModal = ({ isOpen, onClose, webinarId, webinarTitle, webinarUrl }) => {
    const [themes, setThemes] = useState([]);
    const [filteredThemes, setFilteredThemes] = useState([]);
    const [selectedTheme, setSelectedTheme] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [creatingNewTheme, setCreatingNewTheme] = useState(false);
    const [page, setPage] = useState(1);
    const themesPerPage = 8; // Количество тем на одной странице

    useEffect(() => {
        if (isOpen) {
            fetchThemes();
        }
    }, [isOpen]);

    const fetchThemes = async () => {
        setLoading(true);
        try {
            const response = await $host.get("/api/teachers/themes", { withCredentials: true });
            setThemes(response.data || []);
            setFilteredThemes(response.data || []);
            setPage(1); // Сброс на первую страницу при загрузке
        } catch (err) {
            setError(err.response?.data?.message || "Ошибка загрузки тем");
        }
        setLoading(false);
    };

    // Фильтр тем при изменении строки поиска
    useEffect(() => {
        if (!searchQuery) {
            setFilteredThemes(themes);
        } else {
            const lowerQuery = searchQuery.toLowerCase();
            const matchedThemes = themes.filter((theme) =>
                theme.title.toLowerCase().includes(lowerQuery)
            );
            setFilteredThemes(matchedThemes);
            setPage(1); // Сброс на первую страницу при поиске
        }
    }, [searchQuery, themes]);

    const handleAddToTheme = async () => {
        if (!selectedTheme) return;

        try {
            await $host.put(`/api/teachers/themes/${selectedTheme}`, {
                webinar_room_id: webinarId,
                webinar_url: webinarUrl,
            }, { withCredentials: true });

            onClose();
        } catch (error) {
            console.error("Ошибка добавления вебинара в тему:", error.response?.data?.message || error.message);
        }
    };

    const handleThemeCreated = (newTheme) => {
        fetchThemes(); // Обновляем список тем
        setCreatingNewTheme(false); // Закрываем модалку создания
        setSelectedTheme(newTheme.id); // Сразу выбираем созданную тему
    };

    // Пагинация
    const totalPages = Math.ceil(filteredThemes.length / themesPerPage);
    const paginatedThemes = filteredThemes.slice((page - 1) * themesPerPage, page * themesPerPage);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-xl w-[600px] relative">
                    <h2 className="text-xl font-bold mb-4">Выберите тему</h2>

                    {/* Поле поиска */}
                    <input
                        type="text"
                        placeholder="🔎 Поиск темы..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="p-2 border rounded-md w-full mb-4"
                    />

                    {loading && <p className="text-gray-500">Загрузка...</p>}
                    {error && <p className="text-red-500">{error}</p>}

                    {/* Сетка тем */}
                    {paginatedThemes.length > 0 ? (
                        <div className="grid grid-cols-4 gap-3 mb-4">
                            {paginatedThemes.map((theme) => (
                                <ThemeCardSmall
                                    key={theme.id}
                                    id={theme.id}
                                    title={theme.title}
                                    onSelect={setSelectedTheme}
                                    isSelected={selectedTheme === theme.id}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500">Темы не найдены</p>
                    )}

                    {/* Кнопки пагинации */}
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                            <button
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                className="px-4 py-2 bg-gray-300 rounded-md"
                                disabled={page === 1}
                            >
                                Назад
                            </button>
                            <span className="text-gray-700">Страница {page} из {totalPages}</span>
                            <button
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                className="px-4 py-2 bg-gray-300 rounded-md"
                                disabled={page === totalPages}
                            >
                                Вперёд
                            </button>
                        </div>
                    )}

                    {/* Создание новой темы */}
                    <button
                        onClick={() => setCreatingNewTheme(true)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md w-full mt-4"
                    >
                        ➕ Создать новую тему
                    </button>

                    <div className="flex justify-end space-x-4 mt-4">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded-md">Отмена</button>
                        <button
                            onClick={handleAddToTheme}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md"
                            disabled={!selectedTheme}
                        >
                            Добавить
                        </button>
                    </div>
                </div>
            </div>

            {/* Модалка создания новой темы */}
            {creatingNewTheme && (
                <CreateThemeModal
                    isOpen={creatingNewTheme}
                    onClose={() => setCreatingNewTheme(false)}
                    onThemeCreated={handleThemeCreated}
                />
            )}
        </>
    );
};

export default SelectThemeModal;
